/**
 * Header art for a release in the "What's new" pop-up and the changelog:
 * a row of tees on the rack, the middle one framed by the scan corners from
 * the Print Run mark. The ground follows the theme's --primary-strong, so it
 * re-tints with the brand; the tees use the chart palette so it never reads
 * as two flat colours. A release can swap in its own photo (`image:` in the
 * changelog entry), shown instead.
 */
import { cn } from '@/lib/utils';

const TEE =
  'M-21 -24 L-8 -30 Q0 -22 8 -30 L21 -24 L34 -10 L24 0 L18 -5 L18 30 L-18 30 L-18 -5 L-24 0 L-34 -10 Z';

// x, y, scale, fill: chart palette, back row smaller and lower.
const RACK: Array<[number, number, number, string]> = [
  [62, 66, 0.82, '#C27A12'],
  [134, 60, 0.92, '#3B6FD8'],
  [306, 60, 0.92, '#D9574E'],
  [378, 66, 0.82, '#8E4FB8'],
];

interface ReleaseBannerProps {
  image?: string | null;
  alt?: string;
  className?: string;
}

export function ReleaseBanner({ image, alt = '', className }: ReleaseBannerProps) {
  if (image) {
    return (
      <img
        src={image}
        alt={alt}
        className={cn('aspect-[22/7] w-full max-w-full rounded-lg object-cover', className)}
      />
    );
  }
  return (
    <svg
      viewBox="0 0 440 140"
      role="presentation"
      aria-hidden="true"
      className={cn('aspect-[22/7] w-full max-w-full rounded-lg', className)}
      style={{ background: 'hsl(var(--primary-strong))' }}
    >
      <defs>
        <pattern id="rb-dots" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.1" fill="#FFFFFF" fillOpacity="0.12" />
        </pattern>
        <linearGradient id="rb-glow" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.14" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="440" height="140" fill="url(#rb-dots)" />
      <rect width="440" height="140" fill="url(#rb-glow)" />
      {/* rail */}
      <rect x="24" y="26" width="392" height="3" rx="1.5" fill="#FFFFFF" fillOpacity="0.35" />
      {RACK.map(([x, y, s, fill]) => (
        <g key={x} transform={`translate(${x} ${y}) scale(${s})`}>
          <path d="M0 -42 L0 -30" stroke="#FFFFFF" strokeOpacity="0.45" strokeWidth="2" />
          <path d={TEE} fill={fill} />
        </g>
      ))}
      {/* the scanned garment */}
      <g transform="translate(220 74)">
        <path d="M0 -48 L0 -36" stroke="#FFFFFF" strokeOpacity="0.6" strokeWidth="2" />
        <path d={TEE} transform="scale(1.12)" fill="#87D2AE" />
        <rect x="-9" y="2" width="18" height="18" rx="2" fill="#FFFFFF" />
        <path d="M-5 6h4v4h-4zM1 12h4v4h-4zM-5 14h2v2h-2zM3 6h2v2h-2z" fill="#0E4D4C" />
        {/* scan corners, as in the Print Run mark */}
        <g stroke="#FFFFFF" strokeWidth="4" fill="none" strokeLinecap="round">
          <path d="M-52 -26 V-40 Q-52 -48 -44 -48 H-30" />
          <path d="M52 -26 V-40 Q52 -48 44 -48 H30" />
          <path d="M-52 26 V40 Q-52 48 -44 48 H-30" />
          <path d="M52 26 V40 Q52 48 44 48 H30" />
        </g>
        <rect x="-46" y="10" width="92" height="2.5" rx="1.25" fill="#FFFFFF" fillOpacity="0.85" />
      </g>
    </svg>
  );
}
